import { Toast } from "@tento-ui/components/toast/toast";
import { Storybook } from "@tento-ui/ui/storybook";
import { CalendarVisibilitySelector } from "./calendar-visibility-selector";
import {
  Appearance,
  CalendarsClient,
  AuthScopedCalendarId,
  MemoryStorage,
  LonaSdk,
  RequestErrorHandlers,
  DebounceExecutors,
} from "@tento-lona";
import { Network } from "@tento-lona/network";
import { platformOf } from "@tento-lona/host";
import { AuthId, CalendarId, UserId, InvalidationSink, SheetRegistries } from "@tento-lona/sheets";
import { DOM_DEBOUNCE_BRIDGE, DOM_HOST } from "@tento-lona/sheets-ui";

export class CalendarVisibilitySelectorStoryBook extends Storybook.Default {
  title(): string {
    return "Calendar Visibility Selector";
  }

  examples(): Storybook.Example[] {
    const mockCalendars = this.createMockCalendars();
    const mockClient = new LonaSdk.Client({
      storage: new MemoryStorage(),
      host: DOM_HOST,
      registries: SheetRegistries.BUILTINS,
      invalidation: InvalidationSink.create(),
      onSheetPromoted: null,
      baseUrl: null,
      accessToken: null,
      fetch: null,
      rowsOptions: (client) => ({
        backend: client.httpBackend(),
        calendarConfig: LonaSdk.Client.EMPTY_CALENDAR_CONFIG,
        taskListConfig: LonaSdk.Client.EMPTY_TASK_LIST_CONFIG,
        calendarPrefs: null,
      }),
      requestErrorHandler: RequestErrorHandlers.TRACE,
      debounceExecutor: DebounceExecutors.browser(DOM_DEBOUNCE_BRIDGE),
      appearance: Appearance.HEADLESS,
    });
    const mockStore = mockClient.calendars;
    platformOf(mockClient).seedBoot({ user: UserId.of("mock-user"), calendars: mockCalendars, sheets: [] });

    return [
      {
        title: "Default Visibility Mode",
        render: () => {
          return CalendarVisibilitySelector.make()
            .bind("default", [], (mode, calendars) => {
              Toast.info(
                `Mode: ${mode}, Custom calendars: ${calendars.length}`,
              );
            }, mockStore)
            .assignStyles({
              width: "100%",
              maxWidth: "400px",
            });
        },
      },
      {
        title: "Custom Visibility Mode",
        render: () => {
          return CalendarVisibilitySelector.make()
            .bind("custom", [], (mode, calendars) => {
              Toast.info(
                `Mode: ${mode}, Custom calendars: ${calendars.length}`,
              );
            }, mockStore)
            .assignStyles({
              width: "100%",
              maxWidth: "400px",
            });
        },
      },
      {
        title: "Custom Mode with Preselected Calendars",
        render: () => {
          const preselected: AuthScopedCalendarId[] = [
            [AuthId.of("auth1"), CalendarId.of("cal1")],
            [AuthId.of("auth2"), CalendarId.of("cal2")],
          ];
          return CalendarVisibilitySelector.make()
            .bind("custom", preselected, (mode, calendars) => {
              Toast.info(
                `Mode: ${mode}, Custom calendars: ${calendars.length}`,
              );
            }, mockStore)
            .assignStyles({
              width: "100%",
              maxWidth: "400px",
            });
        },
      },
    ];
  }

  private createMockCalendars(): Network.CalendarList[] {
    return [
      {
        authId: Network.AuthId.of("auth1"),
        isPrimary: true,
        calendars: [
          {
            id: CalendarId.of("cal1"),
            summary: "Work Calendar",
            accessRole: "owner",
            capabilities: null,
            colorId: "1",
            source: "google",
            eventsLookupKey: ":~google:auth1:calendar:cal1:events",
          },
          {
            id: CalendarId.of("cal2"),
            summary: "Personal",
            accessRole: "owner",
            capabilities: null,
            colorId: "2",
            source: "google",
            eventsLookupKey: ":~google:auth1:calendar:cal2:events",
          },
          {
            id: CalendarId.of("cal3"),
            summary: "Team Events",
            accessRole: "writer",
            capabilities: null,
            colorId: "3",
            source: "google",
            eventsLookupKey: ":~google:auth1:calendar:cal3:events",
          },
        ],
      },
      {
        authId: Network.AuthId.of("auth2"),
        isPrimary: false,
        calendars: [
          {
            id: CalendarId.of("cal1"),
            summary: "Secondary Calendar",
            accessRole: "owner",
            capabilities: null,
            colorId: "4",
            source: "google",
            eventsLookupKey: ":~google:auth2:calendar:cal1:events",
          },
          {
            id: CalendarId.of("cal2"),
            summary: "Shared Projects",
            accessRole: "reader",
            capabilities: null,
            colorId: "5",
            source: "google",
            eventsLookupKey: ":~google:auth2:calendar:cal2:events",
          },
        ],
      },
      {
        authId: Network.AuthId.of("auth3"),
        isPrimary: false,
        calendars: [
          {
            id: CalendarId.of("cal1"),
            summary: "Marketing Events",
            accessRole: "writer",
            capabilities: null,
            colorId: "6",
            source: "google",
            eventsLookupKey: ":~google:auth3:calendar:cal1:events",
          },
        ],
      },
    ];
  }
}
